import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  cn,
} from "../../shared/ui";
import type {
  EmployeeSummary,
  PageResponse,
  ProjectRole,
  ProjectSummary,
  ResourcePersonCapacity,
} from "../../shared/types/api";
import { PROJECT_ROLE_LABELS } from "../projects/projectVocabulary";
import { defaultWindow, formatHours, formatPercent } from "./resourceVocabulary";

const ROLES = Object.keys(PROJECT_ROLE_LABELS) as ProjectRole[];

/**
 * Allocates somebody's time to a project (§35).
 *
 * <p>The panel shows what the allocation will do to that person's week <em>before</em> it is
 * saved, and updates as the percentage changes. §42 asks for a warning and explicitly not a block;
 * a warning that only appears afterwards is neither, and the moment a planner can still change
 * their mind is while the number is being typed.
 */
export function AllocateDrawer({
  open,
  onClose,
  onSaved,
  fixedProjectId,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** Pre-set when allocating from inside a project. */
  fixedProjectId?: string;
}) {
  const [projectId, setProjectId] = useState(fixedProjectId ?? "");
  const [employeeId, setEmployeeId] = useState("");
  const [projectRole, setProjectRole] = useState<ProjectRole>("DEVELOPER");
  const [percentage, setPercentage] = useState("50");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const projects = useQuery({
    queryKey: ["projects", "picker"],
    queryFn: () =>
      api.get<PageResponse<ProjectSummary>>("/api/v1/projects?size=200&sort=name,asc"),
    enabled: open,
  });

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: open,
  });

  /** What this person's window already looks like — the §42 warning, before committing. */
  const existing = useQuery({
    queryKey: ["person-capacity", employeeId, start, end],
    queryFn: () =>
      api.get<ResourcePersonCapacity>(
        `/api/v1/resources/people/${employeeId}/capacity?from=${start}&to=${effectiveEnd(start, end)}`,
      ),
    enabled: open && Boolean(employeeId) && Boolean(start),
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    const window = defaultWindow();
    setProjectId(fixedProjectId ?? "");
    setEmployeeId("");
    setProjectRole("DEVELOPER");
    setPercentage("50");
    setStart(window.from);
    setEnd(window.to);
    setError(null);
  }, [open, fixedProjectId]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post("/api/v1/resources/allocations", {
        projectId,
        employeeId,
        projectRole,
        allocationStart: start,
        allocationEnd: end || null,
        allocationPercentage: Number(percentage),
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not create the allocation.");
    } finally {
      setSaving(false);
    }
  };

  const proposed = Number(percentage) / 100;
  const current = existing.data?.utilisation ?? 0;
  const projected = current + proposed;
  const wouldOverCommit = existing.data != null && projected > 1.05;

  const backwards = Boolean(start && end) && end <= start;
  const incomplete = !projectId || !employeeId || !start || !percentage;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Allocate time"
      subtitle="A share of somebody's working time, for a period."
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={incomplete || backwards}
          >
            {saving ? "Allocating…" : wouldOverCommit ? "Allocate anyway" : "Allocate"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {!fixedProjectId && (
          <Field label="Project" required>
            <Select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
              <option value="">Choose a project</option>
              {(projects.data?.content ?? []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label="Person" required>
          <Select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)}>
            <option value="">Choose a person</option>
            {(employees.data?.content ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.fullName} — {employee.designation}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Role on the project" required>
            <Select
              value={projectRole}
              onChange={(event) => setProjectRole(event.target.value as ProjectRole)}
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {PROJECT_ROLE_LABELS[role]}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Share of their time"
            required
            hint="A percentage, so it stays right in a week with a holiday in it."
          >
            <Input
              type="number"
              min={1}
              max={100}
              value={percentage}
              onChange={(event) => setPercentage(event.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="From" required>
            <Input
              type="date"
              value={start}
              onChange={(event) => setStart(event.target.value)}
            />
          </Field>
          <Field label="Until" hint="Leave empty for open-ended.">
            <Input
              type="date"
              value={end}
              min={start}
              onChange={(event) => setEnd(event.target.value)}
            />
          </Field>
        </div>

        {backwards && (
          <InlineAlert tone="warning">
            An allocation cannot end on or before the day it starts.
          </InlineAlert>
        )}

        {/* The §42 warning, while there is still time to change the number. */}
        {existing.data && (
          <section className="rounded-lg border border-ink-200 bg-ink-50 px-4 py-3">
            <h3 className="text-sm font-semibold text-ink-900">
              What this does to {existing.data.fullName}
            </h3>

            <div className="mt-2 space-y-2 text-sm">
              <Line label="Available over these dates" value={formatHours(existing.data.availableHours)} />
              <Line
                label="Already committed"
                value={`${formatHours(existing.data.allocatedHours)} · ${formatPercent(current)}`}
              />
              <Line label="This allocation" value={`+${formatPercent(proposed)}`} />
              <div className="border-t border-ink-200 pt-2">
                <Line
                  label="Would become"
                  value={formatPercent(projected)}
                  tone={projected > 1.25 ? "bad" : projected > 1.05 ? "warn" : "good"}
                />
              </div>
            </div>

            {existing.data.byProject.length > 0 && (
              <p className="mt-2 text-xs text-ink-500">
                Already on{" "}
                {existing.data.byProject
                  .map((share) => `${share.projectKey} (${Math.round(share.percentage)}%)`)
                  .join(", ")}
                .
              </p>
            )}

            {existing.data.leaveHours > 0 && (
              <p className="mt-1 text-xs text-ink-500">
                {formatHours(existing.data.leaveHours)} of approved leave falls in this window.
              </p>
            )}
          </section>
        )}

        {wouldOverCommit && (
          <InlineAlert tone="warning">
            This takes them past what exists. That is permitted and sometimes correct — the point
            is that it should be a decision rather than a surprise.
          </InlineAlert>
        )}
      </div>
    </Drawer>
  );
}

/** An open-ended allocation still needs a window to preview against. A month is a fair guess. */
function effectiveEnd(start: string, end: string): string {
  if (end) {
    return end;
  }
  const date = new Date(`${start}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 28);
  return date.toISOString().slice(0, 10);
}

function Line({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "good" | "warn" | "bad";
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-ink-500">{label}</span>
      <span
        className={cn(
          "tnum font-medium",
          tone === "bad"
            ? "text-red-600"
            : tone === "warn"
              ? "text-amber-600"
              : tone === "good"
                ? "text-emerald-600"
                : "text-ink-900",
        )}
      >
        {value}
      </span>
    </div>
  );
}
